import React from 'react';
import type { SequenceFragment, SequenceParticipant, ThemePalette } from '../../types';
import { FRAGMENT_COLORS as FRAG_COLORS } from '../../constants/sequence';

const FRAGMENT_TYPES: ReadonlyArray<SequenceFragment['type']> = ['loop', 'alt', 'opt', 'par'];

export interface FragmentInfoPanelProps {
  palette: Pick<ThemePalette, 'panelBg' | 'panelBorder' | 'panelText' | 'panelSubText'>;
  glass: React.CSSProperties;
  fragment: SequenceFragment;
  participants: SequenceParticipant[];
  onClose: () => void;
  onUpdateFragment: (id: string, updates: Partial<SequenceFragment>) => void;
  onRemoveFragment: (id: string) => void;
  /** Take an undo snapshot before any mutation. */
  snapshotBeforeMutation: () => void;
}

/**
 * Read/edit panel for a sequence-diagram `SequenceFragment` (loop / alt / opt / par).
 *
 * Lets the user change the fragment kind, edit the condition label, expand/contract
 * the participant range (left / right pickers), and delete the fragment.
 */
export const FragmentInfoPanel: React.FC<FragmentInfoPanelProps> = ({
  palette,
  glass,
  fragment: frag,
  participants,
  onClose,
  onUpdateFragment,
  onRemoveFragment,
  snapshotBeforeMutation,
}) => {
  const sorted = [...participants].sort((a, b) => a.x - b.x);
  const currentIds = frag.participantIds;
  const leftmostId = sorted.find((p) => currentIds.includes(p.id))?.id ?? sorted[0]?.id;
  const rightmostId = [...sorted].reverse().find((p) => currentIds.includes(p.id))?.id ?? sorted[sorted.length - 1]?.id;

  const setRange = (newLeftId: string, newRightId: string) => {
    const li = sorted.findIndex((p) => p.id === newLeftId);
    const ri = sorted.findIndex((p) => p.id === newRightId);
    if (li < 0 || ri < 0 || li > ri) return;
    snapshotBeforeMutation();
    onUpdateFragment(frag.id, { participantIds: sorted.slice(li, ri + 1).map((p) => p.id) });
  };

  const accentColor = FRAG_COLORS[frag.type] || '#6366f1';

  return (
    <div
      className="absolute right-4 top-16 bottom-16 z-50 w-72 flex flex-col rounded-2xl shadow-2xl overflow-hidden border backdrop-blur-md"
      style={glass}
    >
      <div
        className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0"
        style={{ borderColor: palette.panelBorder }}
      >
        <div className="flex items-center gap-2">
          <span
            className="text-xs font-bold px-2 py-0.5 rounded uppercase"
            style={{ background: accentColor + '25', color: accentColor }}
          >
            {frag.type}
          </span>
          <span className="text-sm font-semibold" style={{ color: palette.panelText }}>
            Fragment
          </span>
        </div>
        <button onClick={onClose} style={{ color: palette.panelSubText }}>✕</button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: palette.panelSubText }}>Type</label>
          <select
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500/60"
            style={{ color: palette.panelText, borderColor: palette.panelBorder, background: palette.panelBg }}
            value={frag.type}
            onChange={(e) => {
              snapshotBeforeMutation();
              onUpdateFragment(frag.id, { type: e.target.value as SequenceFragment['type'] });
            }}
          >
            {FRAGMENT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: palette.panelSubText }}>Condition / Label</label>
          <input
            type="text"
            className="w-full bg-white/5 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500/60"
            style={{ color: palette.panelText, borderColor: palette.panelBorder }}
            value={frag.label}
            onChange={(e) => onUpdateFragment(frag.id, { label: e.target.value })}
            placeholder="e.g. x > 0"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: palette.panelSubText }}>From Participant</label>
          <select
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500/60"
            style={{ color: palette.panelText, borderColor: palette.panelBorder, background: palette.panelBg }}
            value={leftmostId}
            onChange={(e) => setRange(e.target.value, rightmostId!)}
          >
            {sorted.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: palette.panelSubText }}>To Participant</label>
          <select
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500/60"
            style={{ color: palette.panelText, borderColor: palette.panelBorder, background: palette.panelBg }}
            value={rightmostId}
            onChange={(e) => setRange(leftmostId!, e.target.value)}
          >
            {sorted.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </div>
        <div className="pt-1">
          <div className="flex flex-wrap gap-1.5">
            {sorted.map((p) => {
              const included = currentIds.includes(p.id);
              return (
                <span
                  key={p.id}
                  className="text-[10px] px-2 py-0.5 rounded-full border"
                  style={{
                    background: included ? p.color + '25' : 'transparent',
                    color: included ? p.color : palette.panelSubText,
                    borderColor: included ? p.color + '60' : palette.panelBorder,
                  }}
                >
                  {p.label}
                </span>
              );
            })}
          </div>
          <p className="text-[9px] mt-1.5" style={{ color: palette.panelSubText, opacity: 0.6 }}>Covered participants</p>
        </div>
        <button
          onClick={() => { snapshotBeforeMutation(); onRemoveFragment(frag.id); onClose(); }}
          className="w-full py-2 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors border border-red-500/20"
        >
          Delete Fragment
        </button>
      </div>
    </div>
  );
};
